import { Card, Layout, Spinner } from "@npco/zeller-design-system";
import * as Icon from "@npco/zeller-design-system/icon";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";

import { Layout as ModalLayout } from "../../shared/layout";
import { Prompt } from "../../shared/prompt";
import type { Page } from "./page";
import { PaymentFlow } from "./render/payment-flow";
import { PaymentMethodLinkEmail } from "./render/payment-method-link-email";
import { PaymentMethodLinkSMS } from "./render/payment-method-link-sms";

const defaultLayoutProps = {
	actionsStyle: "full-width",
	logo: true,
} satisfies ModalLayout.Props;

/**
 * The copy behind each terminating page, so the error the host receives says
 * the same thing the merchant read. `PaymentCancelled` and `PaymentExpired`
 * render their title alone; their `message` exists only for the host, because
 * a title that restates the error type tells it nothing.
 */
export const copy = {
	ErrorSetupRequired: {
		title: "Configuration Failure",
		message: "Please run Setup to check your configuration.",
	},
	ErrorNetworkFailure: {
		title: "Network Failure",
		message: "Please check your network connection.",
	},
	ErrorOperationFailure: {
		title: "Operation Failure",
		message:
			"An unrecoverable error occurred when processing this transaction. Please try again.",
	},
	PaymentCancelled: {
		title: "Payment Cancelled",
		message: "The payment was cancelled before it completed.",
	},
	PaymentExpired: {
		title: "Payment Expired",
		message: "The payment link expired before it was paid.",
	},
};

export function render(page: Page) {
	return ADT.switch(page, {
		Initialising: (page) => (
			<ModalLayout {...defaultLayoutProps} onClose={page.onClose}>
				<Prompt image={<Spinner size="lg" />} title="Connecting" />
			</ModalLayout>
		),
		ErrorSetupRequired: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				onClose={page.onClose}
				actions={[{ label: "Close", onClick: page.onClose, primary: true }]}
			>
				<Prompt image={<Illustration.Error />} {...copy.ErrorSetupRequired} />
			</ModalLayout>
		),
		ErrorNetworkFailure: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				onClose={page.onClose}
				actions={[
					{ label: "Try Again", onClick: page.onRetry, primary: true },
					{ label: "Close", onClick: page.onClose },
				]}
			>
				<Prompt image={<Illustration.Error />} {...copy.ErrorNetworkFailure} />
			</ModalLayout>
		),
		ErrorOperationFailure: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				onClose={page.onClose}
				actions={[
					{ label: "Try Again", onClick: page.onRetry, primary: true },
					{ label: "Close", onClick: page.onClose },
				]}
			>
				<Prompt
					image={<Illustration.Error />}
					{...copy.ErrorOperationFailure}
				/>
			</ModalLayout>
		),
		PaymentMethodSelection: (page) => (
			<ModalLayout {...defaultLayoutProps} onClose={page.onClose}>
				<Layout.Container center className="p-32">
					<Layout.Article fixed center>
						<h1 className="text-2xl/heading text-center">
							Select Payment Method
						</h1>
						<Layout.Section>
							<Card
								size="lg"
								title="Send Payment Link via SMS"
								avatar={{ icon: <Icon.ChatBubble />, color: false }}
								onClick={page.onLinkSMS}
							/>
							<Card
								size="lg"
								title="Send Payment Link via Email"
								avatar={{ icon: <Icon.Envelope />, color: false }}
								onClick={page.onLinkEmail}
							/>
						</Layout.Section>
					</Layout.Article>
				</Layout.Container>
			</ModalLayout>
		),
		PaymentMethodLinkEmail: (page) => (
			<PaymentMethodLinkEmail
				page={page}
				defaultLayoutProps={defaultLayoutProps}
			/>
		),
		PaymentMethodLinkSMS: (page) => (
			<PaymentMethodLinkSMS
				page={page}
				defaultLayoutProps={defaultLayoutProps}
			/>
		),
		PaymentFlow: (page) => (
			<PaymentFlow page={page} defaultLayoutProps={defaultLayoutProps} />
		),
		PaymentExpired: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				actions={[{ label: "Close", onClick: page.onClose, primary: true }]}
			>
				<Prompt
					image={<Illustration.Error />}
					title={copy.PaymentExpired.title}
				/>
			</ModalLayout>
		),
		PaymentCancelled: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				actions={[{ label: "Close", onClick: page.onClose, primary: true }]}
			>
				<Prompt
					image={<Illustration.SuccessThumbsUp />}
					title={copy.PaymentCancelled.title}
				/>
			</ModalLayout>
		),
	});
}
